import { cn } from '@/lib/utils';

export interface NotificationBadgeProps {
  /** Unread count. 0/undefined renders no count bubble. */
  count?: number;
  /** Cap the displayed number; values above render as `${max}+`. Default 99. */
  max?: number;
  /** Render the @ mention indicator (Telegram-style). */
  showMention?: boolean;
  /** Extra classes — typically absolute positioning for an overlay. */
  className?: string;
  'aria-label'?: string;
}

/**
 * Reusable count/mention bubble. Distinct from `ui/badge.tsx` (a label primitive)
 * — this is a small overlay pill for unread counts and @-mentions.
 *
 * Renders `null` when there's nothing to show, so callers can mount it
 * unconditionally. Positioning is caller-supplied via `className`, e.g.
 * `absolute -top-1.5 -right-1.5 ring-2 ring-background`.
 */
export function NotificationBadge({
  count = 0,
  max = 99,
  showMention = false,
  className,
  'aria-label': ariaLabel,
}: NotificationBadgeProps) {
  const hasCount = count > 0;
  if (!hasCount && !showMention) return null;

  const display = count > max ? `${max}+` : String(count);

  const defaultLabel = hasCount
    ? showMention
      ? `${count} unread notifications including mentions`
      : `${count} unread notifications`
    : 'mentions';

  return (
    <span
      role="status"
      aria-label={ariaLabel ?? defaultLabel}
      className={cn(
        'inline-flex items-center justify-center gap-0.5 rounded-full',
        'min-w-[18px] h-[18px] px-1.5 text-[10px] font-semibold leading-none',
        // Vivid notification red — pops on both light and dark backgrounds
        // (the theme is otherwise monochrome; red is its only accent hue).
        'bg-red-500 text-white',
        className,
      )}
    >
      {showMention && (
        <span aria-hidden className="font-bold">
          @
        </span>
      )}
      {hasCount && <span>{display}</span>}
    </span>
  );
}
